/*
 * sticky.js — clean-room rebuild (owned, de-vendored)
 * ---------------------------------------------------------------------------
 * Custom elements:
 *   <sticky-group-manager>  StickyGroupManager  (live-core — wraps the header
 *                           group on EVERY page; highest blast radius)
 *   <sticky-announcement>   StickyAnnouncement  (dormant — only emitted when a
 *                           merchant sets an announcement bar's sticky type to
 *                           something other than "none")
 *
 * LOAD CONTRACT
 *   Classic script (NOT an ES module): no import/export. It registers the two
 *   element names below, both behind `if (!customElements.get(...))` guards so
 *   a section re-render in the theme editor can never throw on a duplicate
 *   define. theme-globals.js is loaded before this file but this file needs
 *   nothing from it (no debounce / no focus-trap usage here).
 *
 * COORDINATION CONTRACT WITH <sticky-header> (defined in sections/header.liquid,
 * NOT here — never redefine it):
 *   1. The manager WRITES data-managed-by-group="true" onto every managed
 *      <sticky-header>/<sticky-announcement>. <sticky-header> watches that
 *      attribute via a MutationObserver and, once it flips true, DROPS its own
 *      window scroll handler so only the group drives show/hide. Removing this
 *      write would make the header double-bind its scroll handler.
 *   2. The manager DISPATCHES three CustomEvents — stickyHide / stickyReveal /
 *      stickyReset — on each managed element, all with bubbles:true. The header
 *      listens for stickyHide to close open menu disclosures + the search modal.
 *      Removing/renaming these (or dropping bubbles:true) would leave menus open
 *      when the header hides. stickyReset is dispatched in BOTH the always-sticky
 *      and the normal reset branch, matching the original behaviour.
 *
 * CSS CONTRACT (assets/base.css — do not rename the vars/classes):
 *   Toggled classes: shopify-section-header-sticky, shopify-section-header-hidden,
 *   animate, scrolled-past-header (the last one couples the reading-progress bar
 *   to header state, CSS-only).
 *   Custom props written: --sticky-offset (per element + its content element),
 *   --header-height (header content element + documentElement),
 *   --announcement-bar-height (announcement content element),
 *   --sticky-group-height (documentElement).
 *   Both <sticky-group-manager> and <sticky-announcement> are display:contents,
 *   so the positioned target is the section wrapper, not the custom element.
 */

(function () {
  'use strict';

  var BREAKPOINT_QUERY = '(max-width: 990px)';
  // How long after a scroll-up "prevent reveal" before reveals are allowed
  // again. Mirrors the header's own on-scroll-up timing 1:1.
  var PREVENT_REVEAL_MS = 66;

  /**
   * Coordinates scroll-driven sticky show/hide for the whole header group as a
   * single stacked unit. It takes over each child's positioning, measures
   * heights, stacks per-element top offsets, hides the group on scroll-down /
   * reveals on scroll-up, keeps always-sticky elements pinned, and resets at the
   * top of the page.
   */
  class StickyGroupManager extends HTMLElement {
    connectedCallback() {
      // Idempotent: a section re-render in the editor can re-connect us. Tear
      // down any previous wiring before re-initialising.
      if (this._initialised) {
        this.disconnectedCallback();
      }

      this.stickyElements = this.collectStickyElements();
      if (this.stickyElements.length === 0) {
        // Nothing to manage — install no listeners/observers at all.
        return;
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this._revealTimer = 0;
      this._scrollFrame = 0;

      if (this.stickyElements.length === 1 && this.stickyElements[0].isHeader) {
        this.seedSingleHeaderHeightAndOffsets();
      } else {
        this.updateHeightsAndOffsets();
      }

      // Re-measure when the layout breakpoint is crossed (header height changes).
      this._onBreakpointChange = this.updateHeightsAndOffsets.bind(this);
      this.mediaQuery = window.matchMedia(BREAKPOINT_QUERY);
      this.mediaQuery.addEventListener('change', this._onBreakpointChange);

      // Re-measure when any managed element's content height changes.
      this.resizeObserver = new ResizeObserver(this.updateHeightsAndOffsets.bind(this));
      this.stickyElements.forEach(function (item) {
        if (item.contentElement) this.resizeObserver.observe(item.contentElement);
      }, this);

      this._onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });

      // A descendant (e.g. an open media gallery) can ask us to suppress the
      // next reveal. Listener lives on the manager element itself.
      this._onPreventReveal = function () { this.preventReveal = true; }.bind(this);
      this.addEventListener('preventHeaderReveal', this._onPreventReveal);

      this._initialised = true;
    }

    disconnectedCallback() {
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
        this._onScroll = null;
      }
      if (this.mediaQuery && this._onBreakpointChange) {
        // Store the bound handler once so removeEventListener actually matches
        // (the legacy code passed a fresh .bind() here and leaked the listener).
        this.mediaQuery.removeEventListener('change', this._onBreakpointChange);
      }
      this._onBreakpointChange = null;
      if (this._onPreventReveal) {
        this.removeEventListener('preventHeaderReveal', this._onPreventReveal);
        this._onPreventReveal = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this._revealTimer) {
        clearTimeout(this._revealTimer);
        this._revealTimer = 0;
      }
      if (this._scrollFrame) {
        cancelAnimationFrame(this._scrollFrame);
        this._scrollFrame = 0;
      }
      this._initialised = false;
    }

    /**
     * Discover the managed children, resolve each one's positioned content
     * element, record its flags, and signal ownership. Returned in DOM order
     * (announcement above header) so offset stacking matches the visual stack.
     */
    collectStickyElements() {
      var announcements = Array.prototype.slice.call(this.querySelectorAll('sticky-announcement'));
      var headers = Array.prototype.slice.call(this.querySelectorAll('sticky-header'));
      var all = announcements.concat(headers);

      // Sort by document order so the stack is built top-to-bottom.
      all.sort(function (a, b) {
        var pos = a.compareDocumentPosition(b);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1; // a before b
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;  // a after b
        return 0;
      });

      var items = [];
      all.forEach(function (element) {
        var stickyType = element.dataset.stickyType;
        if (stickyType === 'none') return; // not participating

        var isHeader = element.tagName.toLowerCase() === 'sticky-header';
        // The custom element is display:contents — the positioned target is the
        // surrounding section wrapper.
        var contentElement = isHeader
          ? (element.closest('.section-header') || element.parentElement)
          : (element.closest('.shopify-section') || element.parentElement);

        var isAlwaysSticky = stickyType === 'always' || stickyType === 'reduce-logo-size';

        // LOAD-BEARING: tells <sticky-header> we own its scroll behaviour.
        element.dataset.managedByGroup = 'true';

        // Always-sticky elements are pinned immediately.
        if (isAlwaysSticky && contentElement) {
          contentElement.classList.add('shopify-section-header-sticky');
        }

        items.push({
          element: element,
          contentElement: contentElement,
          isHeader: isHeader,
          predictiveSearch: isHeader ? element.querySelector('predictive-search') : null,
          isAlwaysSticky: isAlwaysSticky,
          height: 0,
          isHidden: false
        });
      });

      return items;
    }

    /**
     * The common one-header layout already publishes its rounded height in CSS.
     * Use that value for the first frame instead of forcing layout during script
     * startup; ResizeObserver still replaces it with the live fractional height.
     */
    seedSingleHeaderHeightAndOffsets() {
      var item = this.stickyElements[0];
      var doc = document.documentElement;
      var seed = window.matchMedia('(max-width: 749px)').matches
        ? item.element.dataset.seedMobileHeight
        : item.element.dataset.seedHeight;
      var height = parseFloat(seed) || 0;
      var ceil = Math.ceil(height) + 'px';

      item.height = height;
      if (item.contentElement) item.contentElement.style.setProperty('--header-height', ceil);
      doc.style.setProperty('--header-height', ceil);
      doc.style.setProperty('--sticky-group-height', ceil);
      this._groupHeight = 0;
      this.updateVisibleOffsets();
    }

    /** Re-measure every element's height and restack the top offsets. */
    updateHeightsAndOffsets() {
      var docStyle = document.documentElement.style;
      var groupHeight = 0;

      this.stickyElements.forEach(function (item) {
        if (!item.contentElement) return;
        var height = item.contentElement.getBoundingClientRect().height;
        item.height = height;
        groupHeight += height;

        var ceil = Math.ceil(height) + 'px';
        if (item.isHeader) {
          item.contentElement.style.setProperty('--header-height', ceil);
          docStyle.setProperty('--header-height', ceil);
        } else {
          item.contentElement.style.setProperty('--announcement-bar-height', ceil);
        }
      });

      docStyle.setProperty('--sticky-group-height', Math.ceil(groupHeight) + 'px');
      // The manager is display:contents and has no box; its old geometry read
      // always returned zero but forced layout after the writes above.
      this._groupHeight = 0;
      this.updateVisibleOffsets();
    }

    /**
     * Stack a running top offset onto each visible element. Hidden elements are
     * skipped so the ones below them slide up into the gap. Lighter recompute
     * used after a hide/reveal/reset without re-measuring heights.
     */
    updateVisibleOffsets() {
      var runningTop = 0;
      this.stickyElements.forEach(function (item) {
        if (!item.contentElement) return;
        var offset = Math.floor(runningTop) + 'px';
        item.element.style.setProperty('--sticky-offset', offset);
        item.contentElement.style.setProperty('--sticky-offset', offset);
        if (!item.isHidden) {
          runningTop += item.height;
        }
      });
    }

    onScroll() {
      // Scroll events can arrive after other code invalidates styles. Reading
      // scrollY in that event then forces layout before the browser's normal
      // frame work. Sample it once inside rAF instead and consume the newest
      // position naturally.
      if (this._scrollFrame) return;
      var self = this;
      this._scrollFrame = requestAnimationFrame(function () {
        self._scrollFrame = 0;
        var scrollTop = window.scrollY || document.documentElement.scrollTop;
        var scrollDirection = scrollTop > self.currentScrollTop ? 1 : (scrollTop < self.currentScrollTop ? -1 : 0);
        self.currentScrollTop = scrollTop;
        if (!self.needsScrollUpdate(scrollTop, scrollDirection)) return;
        self.applyScroll(scrollTop, scrollDirection);
      });
    }

    needsScrollUpdate(scrollTop, scrollDirection) {
      var atTop = scrollTop <= 0;
      var pastGroup = this._groupHeight == null ? null : scrollTop > this._groupHeight;
      var scrollingDown = scrollDirection > 0;
      var scrollingUp = scrollDirection < 0;

      // A missing cached height is exceptional after connectedCallback; let
      // applyScroll use its existing geometry fallback rather than guessing.
      if (!atTop && pastGroup == null) return true;

      for (var i = 0; i < this.stickyElements.length; i++) {
        var item = this.stickyElements[i];
        var content = item.contentElement;

        if (atTop) {
          if (item.isAlwaysSticky) {
            if (content && content.classList.contains('scrolled-past-header')) return true;
          } else if (item.isHidden || (content && (
            content.classList.contains('shopify-section-header-hidden') ||
            content.classList.contains('shopify-section-header-sticky') ||
            content.classList.contains('animate') ||
            content.classList.contains('scrolled-past-header')
          ))) {
            return true;
          }
          continue;
        }

        if (pastGroup) {
          if (content && !content.classList.contains('scrolled-past-header')) return true;
          if (scrollingDown && !item.isAlwaysSticky && !item.isHidden) return true;
          if (scrollingUp) {
            // Repeated frames while reveal is suppressed intentionally refresh
            // the existing 66ms quiet-period timer in applyScroll.
            if (this.preventReveal || (!item.isAlwaysSticky && item.isHidden)) return true;
          }
        } else if (item.isAlwaysSticky) {
          if (content && !content.classList.contains('scrolled-past-header')) return true;
          if (item.isHidden) return true;
        }
      }

      return false;
    }

    applyScroll(scrollTop, scrollDirection) {
      if (window.__menuDrawerOpen === true) return;

      // Don't hide/reveal while a header search is open.
      var searchOpen = this.stickyElements.some(function (item) {
        return item.predictiveSearch && item.predictiveSearch.isOpen;
      });
      if (searchOpen) return;

      var scrollingDown = scrollDirection > 0;
      var scrollingUp = scrollDirection < 0;
      var pastGroup = scrollTop > (this._groupHeight != null ? this._groupHeight : this.getBoundingClientRect().height);
      var atTop = scrollTop <= 0;
      var suppressReveal = pastGroup && scrollingUp && this.preventReveal;

      if (suppressReveal) {
        // Suppress this reveal once, then clear the flag on the same cadence as
        // the legacy header. Do this once per frame, not once per child.
        clearTimeout(this._revealTimer);
        var timerSelf = this;
        this._revealTimer = setTimeout(function () {
          timerSelf.preventReveal = false;
          timerSelf._revealTimer = 0;
        }, PREVENT_REVEAL_MS);
      }

      var self = this;
      var offsetsChanged = false;
      this.stickyElements.forEach(function (item) {
        if (atTop) {
          if (self.resetElement(item)) offsetsChanged = true;
          return;
        }

        if (pastGroup) {
          if (item.contentElement) {
            item.contentElement.classList.add('scrolled-past-header');
          }

          if (scrollingDown) {
            if (self.hideElement(item)) offsetsChanged = true;
          } else if (scrollingUp) {
            if (suppressReveal) {
              if (self.hideElement(item)) offsetsChanged = true;
            } else {
              if (self.revealElement(item)) offsetsChanged = true;
            }
          }
        } else if (item.isAlwaysSticky) {
          // Within the group's own height: keep always-sticky elements pinned.
          if (item.contentElement) {
            item.contentElement.classList.add('scrolled-past-header');
          }
          if (self.revealElement(item)) offsetsChanged = true;
        }
      });

      if (offsetsChanged) this.updateVisibleOffsets();
    }

    hideElement(item) {
      if (item.isAlwaysSticky || item.isHidden) return false; // pinned elements never hide
      if (item.contentElement) {
        item.contentElement.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      }
      item.isHidden = true;
      item.element.dispatchEvent(new CustomEvent('stickyHide', { bubbles: true }));
      return true;
    }

    revealElement(item) {
      if (!item.isHidden) return false;
      if (item.contentElement) {
        item.contentElement.classList.add('shopify-section-header-sticky', 'animate');
        item.contentElement.classList.remove('shopify-section-header-hidden');
      }
      item.isHidden = false;
      item.element.dispatchEvent(new CustomEvent('stickyReveal', { bubbles: true }));
      return true;
    }

    resetElement(item) {
      var content = item.contentElement;

      // Always-sticky elements stay pinned at the top. Reset only when the
      // past-header marker was actually present.
      if (item.isAlwaysSticky) {
        if (!content || !content.classList.contains('scrolled-past-header')) return false;
        content.classList.remove('scrolled-past-header');
        item.element.dispatchEvent(new CustomEvent('stickyReset', { bubbles: true }));
        return false;
      }

      var wasHidden = item.isHidden;
      var needsReset = wasHidden || (content && (
        content.classList.contains('shopify-section-header-hidden') ||
        content.classList.contains('shopify-section-header-sticky') ||
        content.classList.contains('animate') ||
        content.classList.contains('scrolled-past-header')
      ));
      if (!needsReset) return false;

      if (content) {
        content.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'animate',
          'scrolled-past-header'
        );
      }
      item.isHidden = false;
      item.element.dispatchEvent(new CustomEvent('stickyReset', { bubbles: true }));
      return wasHidden;
    }
  }

  /**
   * Lightweight wrapper that lets an announcement bar participate in sticky
   * behaviour. Inside a <sticky-group-manager> the manager fully drives
   * positioning on the section wrapper and this class is a near no-op. On its
   * own (no manager) it self-pins for the "always" type.
   */
  class StickyAnnouncement extends HTMLElement {
    connectedCallback() {
      this.announcementBar = this.querySelector('.announcement-bar');

      // If the manager hasn't claimed us yet (connect-order race) AND we're an
      // "always" bar, self-pin. Harmless if the manager then takes over.
      if (this.dataset.managedByGroup !== 'true') {
        this.initIndependentSticky();
      }

      // Extension points — the manager toggles the real classes on the section
      // wrapper; these self-targeted listeners are intentionally empty hooks.
      this._onHide = function () {};
      this._onReveal = function () {};
      this._onReset = function () {};
      this.addEventListener('stickyHide', this._onHide);
      this.addEventListener('stickyReveal', this._onReveal);
      this.addEventListener('stickyReset', this._onReset);
    }

    disconnectedCallback() {
      if (this._onHide) this.removeEventListener('stickyHide', this._onHide);
      if (this._onReveal) this.removeEventListener('stickyReveal', this._onReveal);
      if (this._onReset) this.removeEventListener('stickyReset', this._onReset);
    }

    initIndependentSticky() {
      if (this.dataset.stickyType === 'always' && this.announcementBar) {
        this.announcementBar.classList.add('shopify-section-header-sticky');
      }
    }
  }

  // Guarded defines: never throw on a duplicate registration (editor re-render).
  if (!customElements.get('sticky-group-manager')) {
    customElements.define('sticky-group-manager', StickyGroupManager);
  }
  if (!customElements.get('sticky-announcement')) {
    customElements.define('sticky-announcement', StickyAnnouncement);
  }

  // Belt-and-suspenders global bindings (harmless; not relied upon by survivors).
  window.StickyGroupManager = StickyGroupManager;
  window.StickyAnnouncement = StickyAnnouncement;
})();
